import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { HelperService } from '../services/helper.service';
import {
  IDLE_SIGNED_OUT_MESSAGE,
  IDLE_SIGNED_OUT_STORAGE_KEY,
} from '../services/idle-timeout.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private router: Router,
    private helper: HelperService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const csrfToken = sessionStorage.getItem('csrf_token') || '';

    /* Pages publiques et assets Angular : pas de jeton (ex. politique de confidentialité chargée via HttpClient). */
    if (
      request.url.includes('/assets/') ||
      request.url.includes('login_microsoft') ||
      request.url.includes('check_session') ||
      request.url.includes('authApi/logout') ||
      request.url.includes('authapi/logout') ||
      request.url.includes('graph.microsoft.com') ||
      request.url.includes(
        'https://aetherfs.sharepoint.com/sites/AFS-PROSPECT/_layouts/15/download.aspx'
      ) ||
      request.url.includes(
        'https://graph.microsoft.com/v1.0/sites/aetherfs.sharepoint.com'
      )
    ) {
      return next.handle(request);
    }

    let token = this.authService.accessToken();
    if (token) {
      request = request.clone({
        setHeaders: {
          Authorization: token,
          'X-CSRF-Token': csrfToken,
        },
      });
    } else {
      this.helper.openSnackBar('Authorization Token Not found', 'error');
      this.authService.clearSessionClientSide();
      void this.router.navigateByUrl(this.authService.getLoginRouterPath());
      return throwError(() => new Error('Authorization Token Not found'));
    }

    const loginPath = this.authService.getLoginRouterPath();
    const isJiraSupportRequest = request.url.includes('jira_support/');

    /**
     * Server-side inactivity backstop: the API destroyed the session before
     * the browser timer fired (clock skew, tab suspended, direct API call).
     * Sign out explicitly so no silent Microsoft reconnection undoes it.
     */
    const handleInactivity = (): void => {
      sessionStorage.setItem(IDLE_SIGNED_OUT_STORAGE_KEY, '1');
      this.helper.openSnackBar(IDLE_SIGNED_OUT_MESSAGE, 'error');
      this.authService.logout(true).subscribe({
        error: () => void this.router.navigateByUrl(loginPath),
      });
    };

    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          if (event.body && event.body.status_code == 401) {
            if (isJiraSupportRequest) {
              return;
            }
            if (event.body.reason === 'inactivity') {
              handleInactivity();
              return;
            }
            this.helper.openSnackBar(event.body.message, 'error');
            this.authService.logout(false).subscribe(() => {
              void this.router.navigateByUrl(loginPath);
            });
          } else if (event.status === 401) {
            if (isJiraSupportRequest) {
              return;
            }
            this.helper.openSnackBar('You are not authorize', 'error');
            this.authService.logout(false).subscribe(() => {
              void this.router.navigateByUrl(loginPath);
            });
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (isJiraSupportRequest && (error.status === 401 || error.status === 502)) {
          return throwError(() => error);
        }
        if (error.status === 401) {
          if (error.error?.reason === 'inactivity') {
            handleInactivity();
            return throwError(() => error);
          }
          this.helper.openSnackBar('Authorization error', 'error');
          this.authService.logout(false).subscribe(() => {
            void this.router.navigateByUrl(loginPath);
          });
        } else if (
          error.error.text === 'Invalid Token' ||
          error.error.text === 'Token Time Expire.' ||
          error.error.text === 'Authorization Token Not found' ||
          error.error.text === 'Token Time Not Define!'
        ) {
          this.helper.openSnackBar(error.error.text, 'error');
          this.authService.logout(false).subscribe(() => {
            void this.router.navigateByUrl(loginPath);
          });
        }
        return throwError(() => error);
      })
    );
  }
}
